import React, { FormEvent, useState, MouseEvent, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';
import { faXmark } from '@fortawesome/free-solid-svg-icons/faXmark';
import { Snackbar } from 'geniebook-ui/dist';

interface IImageInputProps {
  onInsert: (file: File) => void;
  onRemove?: () => void;
  initialFile?: File | false;
}

const MAX_FILE_UPLOAD = 5;
const fileType = ['jpg', 'jpeg', 'png'];
let fileCounter = 0;

const ImageInput = (props: IImageInputProps) => {
  const [imageUrl, setImageUrl] = useState<string>();
  const [showSnackbar, setShowSnackbar] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');

  function handleUpload(event: FormEvent<HTMLInputElement>) {
    if (fileCounter >= MAX_FILE_UPLOAD) return;

    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      if (!fileType.some(el => String(file.type).includes(el))) {
        setSnackbarMessage('File must be in JPG, JPEG, or PNG format.');
        setShowSnackbar(true);
        return;
      }

      if (file.size > 10240000) {
        setSnackbarMessage('Total image size cannot exceed 10 MB');
        setShowSnackbar(true);
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        if (reader.result) {
          setImageUrl(reader.result as string);
        }
      };
      reader.readAsDataURL(file);
      props.onInsert(file);

      fileCounter++;
    }
  }

  function handleRemoveImage(event: MouseEvent) {
    event.stopPropagation();
    setImageUrl(undefined);
    if (props.onRemove) {
      props.onRemove();

      fileCounter--;
    }
  }

  useEffect(() => {
    if (props.initialFile) {
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.result) {
          setImageUrl(reader.result as string);
        }
      };
      reader.readAsDataURL(props.initialFile);
    }
    if (props.initialFile === false) {
      setImageUrl(undefined);
    }
  }, [props.initialFile]);

  // autohide snackbar after 10 secs
  useEffect(() => {
    if (showSnackbar) {
      const timer = setTimeout(() => {
        setShowSnackbar(false);
      }, 10000); // 10 seconds

      return () => clearTimeout(timer);
    }
  }, [showSnackbar]);

  return (
    <div className={'w-[96px] h-[96px] relative rounded-2xl cursor-pointer'}>
      {showSnackbar ? (
        <Snackbar
          message={snackbarMessage}
          buttonText={'Dismiss'}
          onTextClick={() => {
            setShowSnackbar(false);
          }}
          className={'fixed left-1/2 -translate-x-1/2 top-[60px]'}
          states={'error'}
        />
      ) : null}
      <div className={'relative w-full h-full rounded-2xl overflow-hidden'}>
        {fileCounter < MAX_FILE_UPLOAD && (
          <div
            className={
              'bg-primary-navyblue-shade/[0.65] rounded-2xl border border-dashed border-primary-navyblue-bright w-full h-full grid'
            }
          >
            <FontAwesomeIcon
              icon={faPlus}
              className={'fa-2x place-self-center text-primary-navyblue-bright'}
            />
          </div>
        )}
        {imageUrl && (
          <div
            className={
              'absolute z-1 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white min-w-full min-h-full'
            }
          >
            <img
              src={imageUrl}
              alt={''}
              className={'min-w-[96px] min-h-[96px] object-contain'}
            />
          </div>
        )}
        {fileCounter < MAX_FILE_UPLOAD && (
          <input
            className={'w-full h-full opacity-0 absolute left-0 top-0 z-2'}
            type={'file'}
            accept={'image/png, image/jpeg, image/jpg'}
            onInput={handleUpload}
            value={''}
          />
        )}
      </div>
      {imageUrl && (
        <div
          className={
            'absolute -top-[4px] -right-[4px] z-3 w-[16px] h-[16px] rounded-full bg-alert-error border border-white cursor-pointer'
          }
          onClick={handleRemoveImage}
        >
          <div className={'relative w-full h-full'}>
            <FontAwesomeIcon
              icon={faXmark}
              className={
                'fa-2xs text-white absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'
              }
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageInput;
